import React, { useState, useEffect } from "react";
import { useCart } from "../../Modules/Hooks/useCart";
import "./Cart.css";
import {
  Minus,
  Plus,
  X,
  ShoppingBag,
  Truck,
  AlertCircle,
  Check,
  Send,
  Phone,
} from "lucide-react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config";
import Image from "../../Components/Image/Image";
import SEO from "../../Components/SEO/SEO";
import { deliveryService } from "../../Modules/Services/delivery.service";
import { ordersService } from "../../Modules/Services/orders.service";
import settingsService from "../../Modules/Services/settings.service";

/** Кафе ~ 9:00–21:00. Нативный `input type="time"` на iOS Safari часто глючит (пустое value, step). */
const ORDER_TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) break;
      slots.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      );
    }
  }
  return slots;
})();

function nearestFutureSlot(slots) {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  for (const s of slots) {
    const [hh, mm] = s.split(":").map(Number);
    if (hh * 60 + mm >= nowMin) return s;
  }
  return slots[slots.length - 1];
}

const Cart = () => {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    totalItems,
    totalPrice,
    clearCart,
  } = useCart();
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [timeMode, setTimeMode] = useState("asap");
  const [orderTime, setOrderTime] = useState("");
  const [pickupPlace, setPickupPlace] = useState("");
  const [savedPhone, setSavedPhone] = useState("");
  const [savedAddress, setSavedAddress] = useState("");
  const [contacts, setContacts] = useState({
    phoneNumber: null,
    telegram: null,
  });
  const [orderStatus, setOrderStatus] = useState({
    loading: true,
    accepting: true,
    reason: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [createdOrderTotal, setCreatedOrderTotal] = useState(null);
  const [freeDeliveryPriceThreshold, setFreeDeliveryPriceThreshold] =
    useState(2500);
  const [isLoadingThreshold, setIsLoadingThreshold] = useState(false);
  const minimumDeliveryPriceThreshold = 2500;

  useEffect(() => {
    try {
      const fromStorage = localStorage.getItem("shahrizada-phone");
      if (fromStorage) setSavedPhone(fromStorage);

      const savedAddr = localStorage.getItem("shahrizada-address");
      if (savedAddr) setSavedAddress(savedAddr);
    } catch (e) {
      // ignore
    }

    const fetchContacts = async () => {
      try {
        const data = await settingsService.getContacts();
        setContacts({
          phoneNumber: data?.phoneNumber || null,
          telegram: data?.telegram || null,
        });
      } catch (e) {
        // ignore
      }
    };

    const fetchOrderStatus = async () => {
      try {
        setOrderStatus((s) => ({ ...s, loading: true }));
        const data = await settingsService.getOrderStatus();
        setOrderStatus({
          loading: false,
          accepting: Boolean(data?.accepting),
          reason: data?.reason || null,
        });
      } catch (e) {
        setOrderStatus({ loading: false, accepting: true, reason: null });
      }
    };

    fetchContacts();
    fetchOrderStatus();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (deliveryType === "delivery" && address.trim()) {
        fetchDeliveryThreshold(address);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [address, deliveryType]);

  const buildOrderPayload = () => {
    const effectiveTime = timeMode === "asap" ? "по готовности" : orderTime;
    const items = cart.map((item) => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      product_name: item.product_name,
      size: item.size || null,
      weight: item.weight || null,
      price: item.price,
      quantity: item.quantity,
    }));

    if (deliveryType === "pickup") {
      return {
        type: "pickup",
        phone,
        time: effectiveTime,
        pickup_place: pickupPlace,
        items,
      };
    }

    return {
      type: "delivery",
      phone,
      time: effectiveTime,
      delivery: {
        address,
        meta: {
          freeDeliveryThreshold: freeDeliveryPriceThreshold,
          amountToFreeDelivery,
          isFreeDelivery: amountToFreeDelivery === 0,
        },
      },
      items,
    };
  };

  const persistPhone = (value) => {
    try {
      localStorage.setItem("shahrizada-phone", value);
      setSavedPhone(value);
    } catch (e) {
      // ignore
    }
  };

  const persistAddress = (value) => {
    try {
      localStorage.setItem("shahrizada-address", value);
      setSavedAddress(value);
    } catch (e) {
      // ignore
    }
  };

  const handleCreateOrder = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setCreatedOrderId(null);
    setCreatedOrderTotal(null);

    if (!orderStatus.loading && !orderStatus.accepting) {
      setSubmitError(
        orderStatus.reason
          ? `Сейчас нельзя оформить заказ. Причина: ${orderStatus.reason}`
          : "Сейчас нельзя оформить заказ."
      );
      return;
    }

    const isTimeValid = timeMode === "asap" || Boolean(orderTime);
    const isPickupValid = Boolean(phone && isTimeValid);
    const isDeliveryValid = Boolean(phone && address && isTimeValid);
    const isValid =
      deliveryType === "delivery" ? isDeliveryValid : isPickupValid;

    if (!isValid) {
      setSubmitError("Заполните обязательные поля");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildOrderPayload();
      const result = await ordersService.createOrder(payload);
      if (!result?.success) {
        throw new Error(result?.error || "Не удалось создать заказ");
      }

      persistPhone(phone);
      if (deliveryType === "delivery" && address.trim()) {
        persistAddress(address.trim());
      }
      setCreatedOrderId(result.data?.orderId ?? null);
      setCreatedOrderTotal(totalPrice);
      setSubmitSuccess("Заказ создан!");
      clearCart();
    } catch (error) {
      const serverMsg =
        error?.response?.data?.error || error?.response?.data?.message || null;
      const serverReason = error?.response?.data?.reason || null;
      if (error?.response?.status === 403 && (serverMsg || serverReason)) {
        setSubmitError(
          serverReason
            ? `Сейчас нельзя оформить заказ. Причина: ${serverReason}`
            : serverMsg
        );
      } else {
        setSubmitError(error?.message || "Ошибка при создании заказа");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchDeliveryThreshold = async (addr) => {
    if (!addr.trim()) {
      setFreeDeliveryPriceThreshold(minimumDeliveryPriceThreshold);
      return;
    }

    setIsLoadingThreshold(true);
    try {
      const response = await deliveryService.getDeliveryThreshold(addr);
      const threshold =
        response.data?.minOrderAmount || minimumDeliveryPriceThreshold;
      setFreeDeliveryPriceThreshold(threshold);
    } catch (error) {
      console.error("Ошибка при получении порога доставки:", error);
      setFreeDeliveryPriceThreshold(minimumDeliveryPriceThreshold);
    } finally {
      setIsLoadingThreshold(false);
    }
  };

  if (submitSuccess || createdOrderId) {
    const createdOrderTotalFormatted =
      createdOrderTotal != null
        ? `${new Intl.NumberFormat("ru-RU", {
            maximumFractionDigits: 0,
          }).format(Number(createdOrderTotal))} ₽`
        : null;

    return (
      <div className="Cart">
        <SEO title="Заказ принят — Шахризада" />
        <div className="Cart--Success fade-in">
          <div className="Cart--SuccessBadge">
            <Check size={36} strokeWidth={3} />
          </div>
          <h1 className="Cart--SuccessTitle">Заказ принят</h1>
          <p className="Cart--SuccessSub">
            Мы скоро позвоним для уточнения деталей.
          </p>

          {(createdOrderId || createdOrderTotalFormatted) && (
            <div className="Cart--SuccessCard">
              {createdOrderId && (
                <div className="Cart--SuccessRow">
                  <span>Номер заказа</span>
                  <b>№ {createdOrderId}</b>
                </div>
              )}
              {createdOrderTotalFormatted && (
                <div className="Cart--SuccessRow Cart--SuccessRow--total">
                  <span>Сумма</span>
                  <b>{createdOrderTotalFormatted}</b>
                </div>
              )}
            </div>
          )}

          {(contacts?.phoneNumber || contacts?.telegram) && (
            <div className="Cart--ContactsBlock">
              <div className="Cart--ContactsTitle">Связаться с нами</div>
              {contacts?.phoneNumber && (
                <div>
                  <Phone
                    size={14}
                    style={{ verticalAlign: "middle", marginRight: 6 }}
                  />
                  <a href={`tel:${contacts.phoneNumber}`}>
                    {contacts.phoneNumber}
                  </a>
                </div>
              )}
              {contacts?.telegram && (
                <div>
                  <Send
                    size={14}
                    style={{ verticalAlign: "middle", marginRight: 6 }}
                  />
                  <a
                    href={contacts.telegram}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Telegram
                  </a>
                </div>
              )}
            </div>
          )}

          <Link to="/" className="accent--button">
            Вернуться в меню
          </Link>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="Cart">
        <SEO title="Корзина пуста — Шахризада" />
        <div className="Cart--Empty fade-in">
          <div className="Cart--EmptyIcon">
            <ShoppingBag size={36} />
          </div>
          <h2>Корзина пуста</h2>
          <p>Добавьте что-нибудь вкусное из меню, чтобы оформить заказ.</p>
          <Link to="/" className="accent--button">
            Перейти в меню
          </Link>
        </div>
      </div>
    );
  }

  const isTimeValid = timeMode === "asap" || Boolean(orderTime);
  const isPickupFormValid = Boolean(phone && isTimeValid);
  const isDeliveryFormValid = Boolean(phone && address && isTimeValid);
  const isFormValid =
    deliveryType === "delivery" ? isDeliveryFormValid : isPickupFormValid;
  const isOrderingDisabled = !orderStatus.loading && !orderStatus.accepting;

  const amountToFreeDelivery = Math.max(
    0,
    freeDeliveryPriceThreshold - totalPrice
  );

  return (
    <div className="Cart fade-in">
      <SEO title="Корзина — Шахризада" />

      <div className="Cart--Header">
        <h1>Ваш заказ</h1>
        <p>{totalItems} {totalItems === 1 ? "товар" : "товаров"} в корзине</p>
      </div>

      <div className="Cart--Layout">
        <div className="Cart--Items">
          {cart.map((item) => (
            <article key={item.variant_id} className="Cart--Item">
              <Image
                src={`${API_BASE_URL}${item.image_url}`}
                alt={item.product_name}
                className="Cart--ItemImg"
              />
              <div className="Cart--ItemBody">
                <h4 className="Cart--ItemName">{item.product_name}</h4>
                {(item.size || item.weight) && (
                  <span className="Cart--ItemMeta">
                    {item.size}
                    {item.size && item.weight ? " · " : ""}
                    {item.weight ? `${item.weight} г` : ""}
                  </span>
                )}
                <div className="Cart--ItemFooter">
                  <div className="Cart--ItemQty">
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(item.variant_id, item.quantity - 1)
                      }
                      aria-label="Уменьшить"
                    >
                      <Minus size={14} />
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(item.variant_id, item.quantity + 1)
                      }
                      aria-label="Увеличить"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="Cart--ItemPrice">
                    {(item.price * item.quantity).toFixed(0)} ₽
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="Cart--ItemRemove"
                onClick={() => removeFromCart(item.variant_id)}
                aria-label="Удалить товар"
              >
                <X size={18} />
              </button>
            </article>
          ))}
        </div>

        <aside className="Cart--Summary">
          <h3 className="Cart--SummaryTitle">Итог заказа</h3>
          <div className="Cart--Row">
            <span>Товары ({totalItems} шт.)</span>
            <span>{totalPrice.toFixed(0)} ₽</span>
          </div>
          <div className="Cart--Row Cart--RowTotal">
            <span>Итого</span>
            <span>{totalPrice.toFixed(0)} ₽</span>
          </div>

          <div>
            <div className="Cart--SectionLabel">Способ получения</div>
            <div className="Cart--Tabs">
              <button
                type="button"
                className={`Cart--Tab ${
                  deliveryType === "pickup" ? "is-active" : ""
                }`}
                onClick={() => setDeliveryType("pickup")}
              >
                <ShoppingBag size={16} /> Самовывоз
              </button>
              <button
                type="button"
                className={`Cart--Tab ${
                  deliveryType === "delivery" ? "is-active" : ""
                }`}
                onClick={() => setDeliveryType("delivery")}
              >
                <Truck size={16} /> Доставка
              </button>
            </div>
          </div>

          {isOrderingDisabled && (
            <div className="Cart--OrderClosed">
              <div className="Cart--OrderClosedHead">
                <AlertCircle size={18} />
                Приём заказов приостановлен
              </div>
              <div className="Cart--OrderClosedText">
                {orderStatus.reason
                  ? `Причина: ${orderStatus.reason}`
                  : "Попробуйте оформить заказ позже."}
              </div>
            </div>
          )}

          {savedPhone && !phone && (
            <div className="Cart--Saved">
              <div className="Cart--SavedText">
                Сохранённый телефон: <b>{savedPhone}</b>
              </div>
              <button
                type="button"
                className="Cart--SavedBtn"
                onClick={() => setPhone(savedPhone)}
              >
                Использовать
              </button>
            </div>
          )}

          {deliveryType === "delivery" && savedAddress && !address && (
            <div className="Cart--Saved">
              <div className="Cart--SavedText">
                Сохранённый адрес: <b>{savedAddress}</b>
              </div>
              <button
                type="button"
                className="Cart--SavedBtn"
                onClick={() => setAddress(savedAddress)}
              >
                Использовать
              </button>
            </div>
          )}

          <div className="Cart--Form">
            <input
              type="tel"
              placeholder="Номер телефона"
              className="Cart--Input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />

            <div className="Cart--TimeOptions">
              <button
                type="button"
                className={`Cart--TimeOption ${
                  timeMode === "asap" ? "is-active" : ""
                }`}
                onClick={() => setTimeMode("asap")}
              >
                По готовности
              </button>
              <button
                type="button"
                className={`Cart--TimeOption ${
                  timeMode === "scheduled" ? "is-active" : ""
                }`}
                onClick={() => {
                  setTimeMode("scheduled");
                  setOrderTime((prev) =>
                    ORDER_TIME_SLOTS.includes(prev)
                      ? prev
                      : nearestFutureSlot(ORDER_TIME_SLOTS)
                  );
                }}
              >
                В определённое время
              </button>
            </div>
            {timeMode === "scheduled" && (
              <div className="Cart--TimeField">
                <label className="Cart--FieldLabel" htmlFor="cart-order-time">
                  К какому времени
                </label>
                <select
                  id="cart-order-time"
                  className="Cart--Input Cart--Select"
                  value={orderTime}
                  onChange={(e) => setOrderTime(e.target.value)}
                >
                  {ORDER_TIME_SLOTS.map((t) => (
                    <option key={t} value={t}>
                      К {t}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {deliveryType === "pickup" ? (
              <input
                type="text"
                placeholder="Номер столика или кабинки (необязательно)"
                className="Cart--Input"
                value={pickupPlace}
                onChange={(e) => setPickupPlace(e.target.value)}
              />
            ) : (
              <input
                type="text"
                placeholder="Улица, дом, квартира"
                className="Cart--Input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </div>

          {deliveryType === "delivery" && (
            <>
              {isLoadingThreshold && (
                <div className="Cart--Notice Cart--Notice--info">
                  Рассчитываем условия доставки...
                </div>
              )}
              {!isLoadingThreshold && amountToFreeDelivery > 0 && (
                <div className="Cart--Notice Cart--Notice--info">
                  Ещё {amountToFreeDelivery.toFixed(0)} ₽ до бесплатной доставки
                </div>
              )}
              {!isLoadingThreshold && amountToFreeDelivery === 0 && (
                <div className="Cart--Notice Cart--Notice--success">
                  Бесплатная доставка
                </div>
              )}
            </>
          )}

          {submitError && (
            <div className="Cart--Notice Cart--Notice--error">
              {submitError}
            </div>
          )}

          <button
            type="button"
            className="Cart--Checkout"
            onClick={handleCreateOrder}
            disabled={!isFormValid || isSubmitting || isOrderingDisabled}
          >
            {isSubmitting ? "Оформляем..." : `Оформить заказ · ${totalPrice.toFixed(0)} ₽`}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
